import { useState, useEffect } from 'react';
import { searchContext, userAppContext } from '../../../context/Context';
import { advancedExamples } from '../AdvancedExamples';
import useFetchSuggestions from '../../../hooks/useFetchSuggestions';
import { validateInput } from '../AdvancedSearchHelpers';
import { useLocation } from 'react-router-dom';

interface SearchItem {
  diseaseUMLSCUI?: string;
  strID?: string;
  geneID?: string;
  chemID?: string;
  symbol?: string;
  enrichmentSymbol?: string;
  normalizedId?: string;
  [key: string]: any;
}

export const useAdvancedSearch = () => {
  const { setSearchItems } = searchContext();
  const { authorization, isAuthenticated, restrictSources } = userAppContext();
  const [searchType, setSearchType] = useState('DISEASES');
  const [selectedFilter, setSelectedFilter] = useState<string>('');
  const [text, setText] = useState('');
  const [selectedItems, setSelectedItems] = useState<SearchItem[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showTextarea, setShowTextarea] = useState(true);
  const [foundItems, setFoundItems] = useState<SearchItem[]>([]);
  const [notFoundItems, setNotFoundItems] = useState<SearchItem[]>([]);
  const [showFoundItems, setShowFoundItems] = useState(false);
  const [exceededLimit, setExceededLimit] = useState(false);
  const location = useLocation();

  const {
    suggestionsData,
    loading,
    error,
    foundItems: fetchedFoundItems,
    notFoundItems: fetchedNotFoundItems,
    fetchSuggestions,
  } = useFetchSuggestions(searchType, selectedFilter, authorization, isAuthenticated);

  useEffect(() => {
    resetSearch();
  }, [searchType, selectedFilter]);

  useEffect(() => {
    if (location.state?.searchType) {
      setSearchType(location.state.searchType);
    }
    if (location.state?.text) {
      setText(location.state.text);
    }
    if (location.state?.selectedFilter) {
      setSelectedFilter(location.state.selectedFilter);
    }
  }, [location.state]);

  const resetSearch = () => {
    setSearchItems([]);
    setText('');
    setShowSuggestions(false);
    setShowFoundItems(false);
    setShowTextarea(true);
    setSelectedItems([]);
    setNotFoundItems([]);
  };

  const handleQueryChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    setShowSuggestions(false);
  };

  const handleSearchClick = async () => {
    if (!text.trim()) {
      alert('Please provide input text');
      return;
    }

    const inputs = text
      .split('\n')
      .map(line => line.trim())
      .filter(line => line);

    if (inputs.length > 100) {
      setExceededLimit(true);
      setShowTextarea(true);
      alert('Search limit of 100 items exceeded.');
      return;
    } else {
      setExceededLimit(false);
    }

    if (inputs.length) {
      await fetchSuggestions(inputs);
      setShowTextarea(false);
      setShowSuggestions(true);
    } else {
      resetSearch();
    }
  };

  const handleBackClick = () => {
    resetSearch();
  };

  const handleInputValidation = (input: any) => {
    const isValid = validateInput(input, searchType, selectedFilter);
    return isValid;
  };

  const handleCheckboxSelection = (item: SearchItem) => {
    setSelectedItems(prevItems => {
      const itemString = JSON.stringify(item);
      const isSelected = prevItems.some(selected => JSON.stringify(selected) === itemString);

      if (isSelected) {
        return prevItems.filter(selected => JSON.stringify(selected) !== itemString);
      } else {
        return [...prevItems, item];
      }
    });
  };

  const handleSearchSelectedItems = () => {
    if (selectedItems.length === 0) {
      alert('Please select at least one item to search.');
      return;
    }

    setFoundItems([]);
    setShowFoundItems(false);

    const uniqueSelectedItems = selectedItems.filter(
      (item: SearchItem, index: number, self: SearchItem[]) => {
        const itemString = JSON.stringify(item);
        return index === self.findIndex((t: SearchItem) => JSON.stringify(t) === itemString);
      },
    );

    const itemsString = uniqueSelectedItems
      .map((item: SearchItem) => {
        if (!item) return '';
        switch (searchType) {
          case 'DISEASES':
            return item.normalizedId || item.diseaseUMLSCUI || '';
          case 'VARIANTS':
            return item.strID || '';
          case 'GENES':
            // Use geneID as the primary identifier for all gene searches
            // The backend expects geneID for proper resolution
            return item.geneID || item.normalizedId || '';
          case 'CHEMICALS':
            return item.chemID || item.normalizedId || '';
          default:
            return '';
        }
      })
      .filter(id => id)
      .join('-');

    const tab =
      searchType === 'VARIANTS'
        ? 'VARIANTS'
        : searchType === 'DISEASES'
          ? 'DISEASES'
          : searchType === 'GENES'
            ? 'GENES'
            : searchType === 'CHEMICALS'
              ? 'CHEMICALS'
              : 'GDA';
    const source = restrictSources === 'CURATED' ? 'CURATED' : 'ALL';

    const url = `/search?view=${searchType}&idents=${itemsString}&source=${source}&tab=${tab}`;
    window.location.href = url;

    setFoundItems(uniqueSelectedItems);
    setShowFoundItems(true);
  };

  const handleClearAll = () => {
    setSelectedItems([]);
    setFoundItems([]);
  };

  const clearInputText = () => {
    setText('');
  };

  const showExamples = () => {
    const exampleItems = advancedExamples[searchType]?.[selectedFilter] || [];
    setText(exampleItems.join('\n'));
  };

  const handleSelectAll = () => {
    const allResults = suggestionsData.flatMap((item: any) => item.results) as SearchItem[];
    setSelectedItems(allResults);
  };

  return {
    // State
    searchType,
    setSearchType,
    selectedFilter,
    setSelectedFilter,
    text,
    selectedItems,
    showSuggestions,
    showTextarea,
    foundItems,
    notFoundItems,
    showFoundItems,
    exceededLimit,
    suggestionsData,
    loading,
    error,

    // Handlers
    handleQueryChange,
    handleSearchClick,
    handleBackClick,
    handleInputValidation,
    handleCheckboxSelection,
    handleSearchSelectedItems,
    handleClearAll,
    clearInputText,
    showExamples,
    handleSelectAll,
    resetSearch,
  };
};
